// =============================================================================
// Optim TURPE V2 — L'ÉCHEC NOMMÉ QUI GARDE SES LEVIERS (DEV-1913, bloquant B9,
// constat F7.3, lot DEV-1912).
//
// Ce que le client doit vivre : après un refus, il lit la cause vraie et il peut
// encore agir. Un écran d'échec qui grise ses propres leviers transforme une
// valeur corrigeable en cul-de-sac, et le client écrit au support (DEV-1652,
// DEV-1730, DEV-1860 : à chaque fois un forçage manuel one-off).
//
// Deux règles tenues ici :
// 1. **La cause servie passe avant tout.** Le message du moteur, puis son code,
//    puis notre proposition de libellé. La page n'invente jamais une cause.
// 2. **Tous les leviers restent actifs.** `LEVIERS_TOUJOURS_ACTIFS` n'est pas un
//    drapeau à basculer : c'est la règle, écrite pour qu'on ne la reperde pas.
//    `details.levier` sert à METTRE EN AVANT le levier utile, jamais à éteindre
//    les autres.
// =============================================================================

import { ServiceError, getUserFacingDescription } from "@core/exceptions"
import {
    LIBELLE_ETUDE_SANS_DOCUMENT,
    LIBELLE_LEVIER,
    LIBELLE_REFUS,
} from "@modules/courtage/utils/optim-turpe-v2.libelles"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

/**
 * Après un échec, la page laisse TOUS ses leviers actionnables. Constante nommée
 * plutôt que commentaire : un futur « griser pendant l'erreur » se verra.
 */
export const LEVIERS_TOUJOURS_ACTIFS = true

/** L'erreur servie sur une étude, telle que `homeys_analyse` l'a persistée. */
type ErreurServie = {
    code?: string
    message?: string
    description?: string | null
    meilleure_periode?: { date_from?: string; date_to?: string } | null
    levier?: string
    segment?: string
    segments_couverts?: string[]
    details?: Record<string, unknown>
}

export type EchecLu = {
    /** Code servi par le moteur (`error.code`). */
    code: string | null
    /** Phrase à afficher : celle du moteur si elle existe, sinon notre proposition. */
    message: string | null
    /** Vrai quand la phrase vient du moteur et non de la table de la page. */
    messageServi: boolean
    /** Levier servi (`error.levier`), ou `null` s'il n'est pas servi. */
    levier: string | null
    /** Invitation à agir, dérivée du levier servi. `null` si le levier est inconnu. */
    levierLibelle: string | null
    /** Segment refusé, quand la cause est un segment hors périmètre. */
    segment: string | null
    /** Les segments couverts, servis avec le refus par le lot DEV-1912. */
    segmentsCouverts: string[] | null
    /** Ce que le client peut faire pour lever le refus, ou `null` (DEV-2163). */
    remede: string | null
    /** Vrai quand le remède vient du moteur, faux quand il vient de la table de la page. */
    remedeServi: boolean
    /** La meilleure période disponible servie avec le refus (`error.meilleure_periode`), ou `null`. */
    meilleurePeriode: { date_from: string; date_to: string } | null
}

/**
 * Le REMÈDE de chaque refus qui se lève depuis la page (DEV-2163, DEV-2164).
 *
 * Les phrases reprennent le catalogue du moteur (`mixins/runerrors.json` de
 * courtage, champ `description`). Relevé en production le 25/09/2026 : le
 * document d'une étude refusée ne persiste que `code`, `levier` et `message`,
 * sans cette description. Tant que le moteur ne la persiste pas, la page la
 * porte, et une explication servie par la façade passe toujours avant.
 */
export const REMEDE_PAR_CAUSE: Record<string, string> = {
    "donnee-classe-hors-fenetre":
        "Élargissez la période à une année complète, ou choisissez une période qui couvre les deux saisons (par exemple de février à avril).",
    "collecte-interrompue":
        "Choisissez une période couverte par l'historique disponible.",
    // DEV-2311 : levier du catalogue du moteur (`levier_libelle`), non persisté.
    "compteur-non-electrique-hors-perimetre":
        "Choisissez un compteur d'électricité du site, puis lancez l'étude.",
    error_optimturpe_compteur_non_electrique:
        "Choisissez un compteur d'électricité du site, puis lancez l'étude.",
    // Revue témoin du 15/10/2026 (D17, Q28) : les deux refus C5 disent quoi
    // faire ensuite, d'après la description du catalogue du moteur
    // (`runerrors.json`). La puissance maximale quotidienne d'Enedis ne se
    // saisit pas (`completer.ts`) : le remède est une autre période.
    "param-puissance-max-c5-manquante":
        "Une autre période peut lever le refus : choisissez-la dans « Modifier les hypothèses », puis relancez l'étude.",
    "param-puissance-max-hors-catalogue-c5":
        "Faites étudier l'évolution du raccordement (branchement ou puissance de raccordement) : changer la puissance souscrite ne suffit pas.",
}

/**
 * Les causes dont le remède se fait HORS de la page (D17) : la page le dit,
 * mais aucune hypothèse à modifier ne lève le refus ; il reste sans levier.
 */
export const CAUSES_AU_REMEDE_HORS_PAGE: ReadonlySet<string> = new Set([
    "param-puissance-max-hors-catalogue-c5",
])

/**
 * Le texte d'un rapport ou d'une courbe refusés parce que l'étude n'a pas de
 * résultat (DEV-2311, 409 nommés par le moteur) : la description servie, sinon
 * le libellé de la page. `null` pour toute autre erreur, que l'appelant traite
 * comme avant.
 */
export function messageEtudeSansDocument(error: unknown): string | null {
    if (!(error instanceof ServiceError) || error.status !== 409) return null
    const libelle = LIBELLE_ETUDE_SANS_DOCUMENT[error.code ?? ""]
    if (!libelle) return null
    return getUserFacingDescription(error) ?? libelle
}

/**
 * L'échec d'une étude, ou `null` quand l'étude n'a pas échoué.
 *
 * Un `FAILURE` sans code ni message n'est pas taillé en silence : il rend un
 * `EchecLu` dont tous les champs sont `null`, et la page dit qu'elle ne connaît
 * pas la cause. Dire « je ne sais pas » est une information ; afficher une panne
 * générique à la place d'une cause servie serait un mensonge (DEV-1596 : 112
 * échecs sur 217 attribués au client alors que la cause était chez nous).
 */
export function lireEchec(etude: OptimTurpeV2Etude): EchecLu | null {
    const erreur = etude.error as ErreurServie | undefined
    const enEchec = etude.status === "FAILURE" || Boolean(erreur?.code)
    if (!enEchec) return null

    const code = erreur?.code ?? null
    const details = (erreur?.details ?? {}) as Record<string, unknown>
    const messageServi =
        typeof erreur?.message === "string" && erreur.message.trim() !== ""
            ? erreur.message
            : null
    const propose = code ? (LIBELLE_REFUS[code] ?? null) : null

    const levier =
        (typeof erreur?.levier === "string" ? erreur.levier : null) ??
        (typeof details.levier === "string" ? details.levier : null)
    const segment =
        (typeof erreur?.segment === "string" ? erreur.segment : null) ??
        (typeof details.segment === "string" ? details.segment : null)
    const couverts = erreur?.segments_couverts ?? details.segments_couverts
    const remedeServi =
        typeof erreur?.description === "string" &&
        erreur.description.trim() !== ""
            ? erreur.description
            : null

    return {
        code,
        message: messageServi ?? propose,
        messageServi: messageServi !== null,
        levier,
        levierLibelle: levier ? (LIBELLE_LEVIER[levier] ?? null) : null,
        segment,
        segmentsCouverts: Array.isArray(couverts)
            ? (couverts as string[])
            : null,
        remede: remedeServi ?? (code ? (REMEDE_PAR_CAUSE[code] ?? null) : null),
        remedeServi: remedeServi !== null,
        meilleurePeriode:
            erreur?.meilleure_periode?.date_from &&
            erreur?.meilleure_periode?.date_to
                ? {
                      date_from: erreur.meilleure_periode.date_from,
                      date_to: erreur.meilleure_periode.date_to,
                  }
                : null,
    }
}

/** Un levier servi avec le refus, prêt à afficher (DEV-2189). */
export type LevierDuRefus = {
    code: string | null
    libelle: string
    /** La période à poser (`champs.data.date_from/date_to`), ou `null` : une phrase. */
    periode: { date_from: string; date_to: string } | null
}

const DATE_ISO = /^\d{4}-\d{2}-\d{2}$/

/**
 * Le levier que le moteur sert pour renvoyer au relais « Faire relire cette
 * étude » (courtage `LEVIER_RELAIS_SUPPORT`). Ce bloc est retiré de la page
 * (DEV-2311, décision du 05/10/2026) : ce levier n'est plus jamais affiché.
 */
export const LEVIER_RELAIS_SUPPORT = "relais-support"

/**
 * Les leviers servis avec un refus (`error.leviers`, courtage v154), dans
 * l'ordre servi. Un levier qui porte `data.date_from` et `data.date_to`
 * devient un bouton qui relance sur ces dates ; les autres se lisent en
 * phrase. Un levier sans libellé n'est pas affiché : la page n'invente pas de
 * texte. Le levier du relais n'est pas rendu (`LEVIER_RELAIS_SUPPORT`).
 */
export function lireLeviersDuRefus(
    etude: OptimTurpeV2Etude | null | undefined
): LevierDuRefus[] {
    const leviers = etude?.error?.leviers
    if (!Array.isArray(leviers)) return []
    return leviers.flatMap((levier) => {
        if (levier?.code === LEVIER_RELAIS_SUPPORT) return []
        const libelle =
            typeof levier?.libelle === "string" ? levier.libelle.trim() : ""
        if (!libelle) return []
        const debut = levier.champs?.["data.date_from"]
        const fin = levier.champs?.["data.date_to"]
        const periode =
            typeof debut === "string" &&
            typeof fin === "string" &&
            DATE_ISO.test(debut) &&
            DATE_ISO.test(fin)
                ? { date_from: debut, date_to: fin }
                : null
        return [
            {
                code: typeof levier.code === "string" ? levier.code : null,
                libelle,
                periode,
            },
        ]
    })
}

/**
 * Le refus se corrige-t-il depuis cette page ?
 *
 * `levier: "aucun"` est une réponse pleine du moteur : il n'y a rien à corriger
 * ici (un compteur résilié, un segment non couvert). L'écran le dit, et garde
 * malgré tout ses leviers actionnables : le client peut changer de compteur ou
 * de période sans recharger la page.
 */
export function refusCorrigeableIci(echec: EchecLu | null): boolean {
    if (!echec) return false
    return echec.levier !== null && echec.levier !== "aucun"
}

// =============================================================================
// Refus indépendants du scénario (DEV-2189, revue I8, courtage#256).
//
// Un compteur résilié, ou dont la collecte est arrêtée avant la période
// demandée, est refusé quel que soit le scénario. Quand la page le sait déjà,
// elle n'envoie qu'un POST au lieu de la paire « sans / avec dépassements ».
// =============================================================================

/** Les refus qui ne dépendent pas du scénario (`precontrole_paire`, courtage#256). */
export const CODES_REFUS_INDEPENDANTS_DU_SCENARIO = [
    "compteur-resilie",
    "collecte-interrompue",
    // Saisies robustes (20/10/2026) : les deux refus de l'état de la source
    // (courtage `validate_etat_source.py`), listés par `precontrole_paire.py`.
    "compteur-identifiant-inconnu",
    "compteur-donnees-non-recues",
]

/** Les refus qu'une formule tarifaire saisie lève (courtage DEV-2437). */
const CODES_REFUS_LEVES_PAR_UNE_FORMULE = [
    "compteur-identifiant-inconnu",
    "compteur-donnees-non-recues",
]

/** Ce que le lancement saisit et que le moteur lit avant ces refus. */
export type SaisiesDuLancement = {
    fta?: string | null
    segment?: string | null
}

/**
 * La formule et le segment SAISIS dans un corps de lancement ou des
 * surcharges de rejeu (`structure_tarifaire.formule_tarifaire_acheminement.fta`,
 * `compteur.segment`), comme les lit le moteur.
 */
export function saisiesDuLancement(corps: unknown): SaisiesDuLancement {
    const c = (corps ?? {}) as {
        structure_tarifaire?: {
            formule_tarifaire_acheminement?: { fta?: unknown } | null
        } | null
        compteur?: { segment?: unknown } | null
    }
    const fta = c.structure_tarifaire?.formule_tarifaire_acheminement?.fta
    const segment = c.compteur?.segment
    return {
        fta: typeof fta === "string" && fta ? fta : null,
        segment: typeof segment === "string" && segment ? segment : null,
    }
}

export type RefusConnu = {
    code: string
    /** Date ISO de l'arrêt de la collecte (`error.details.collecte_interrompue_depuis_iso`). */
    depuisIso: string | null
}

/**
 * Le refus indépendant du scénario que portent ces études (un lancement), ou
 * `null`. La date d'arrêt est lue dans `error.details` (ajout additif de
 * courtage#256) ; elle n'est jamais extraite de la phrase.
 */
export function refusConnuDe(
    etudes: Array<OptimTurpeV2Etude | null | undefined>
): RefusConnu | null {
    for (const etude of etudes) {
        const code = etude?.error?.code
        if (
            etude?.status !== "FAILURE" ||
            !code ||
            !CODES_REFUS_INDEPENDANTS_DU_SCENARIO.includes(code)
        ) {
            continue
        }
        const iso = etude.error?.details?.collecte_interrompue_depuis_iso
        return {
            code,
            depuisIso:
                typeof iso === "string" && /^\d{4}-\d{2}-\d{2}/.test(iso)
                    ? iso.slice(0, 10)
                    : null,
        }
    }
    return null
}

/**
 * Le refus connu s'applique-t-il à une période qui commence le `dateFrom` ?
 * Même règle que le moteur : une collecte arrêtée ne refuse que si l'arrêt
 * précède le début de la période. Sans début explicite (fenêtre par défaut,
 * celle du refus constaté), le refus s'applique. Un compteur résilié est
 * refusé quelle que soit la période.
 */
export function refusSApplique(
    refus: RefusConnu | null | undefined,
    dateFrom: string | null | undefined,
    saisies: SaisiesDuLancement = {}
): boolean {
    if (!refus) return false
    // Relecture sceptique de la PR #858 : les deux causes d'une formule
    // absente nommées par la collecte (courtage
    // `raise_si_collecte_empeche_la_fta`) ne se lèvent que SANS formule
    // saisie, et, hors numéro inconnu, sans segment connu. Une formule ou un
    // segment saisis ensuite rétablissent la paire de lancements.
    if (CODES_REFUS_LEVES_PAR_UNE_FORMULE.includes(refus.code) && saisies.fta) {
        return false
    }
    if (refus.code === "compteur-donnees-non-recues" && saisies.segment) {
        return false
    }
    if (refus.code !== "collecte-interrompue") return true
    if (!dateFrom) return true
    if (!refus.depuisIso) return false
    return refus.depuisIso < dateFrom.slice(0, 10)
}

/** « collecte arrêtée le 12/03/2024 », pour le sélecteur de compteurs, ou `null`. */
export function libelleCollecteArretee(
    refus: RefusConnu | null | undefined
): string | null {
    if (refus?.code !== "collecte-interrompue" || !refus.depuisIso) return null
    const [a, m, j] = refus.depuisIso.split("-")
    return `collecte arrêtée le ${j}/${m}/${a}`
}
